import "./DetectionTool.css";
import {
  Button,
  Container,
  Box,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Alert,
  Collapse,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import Search from "../Search";
import TrialAccordion from "../TrialAccordion";
import TrialDetails from "../TrialDetails";
import ProjectIntroduction from "../ProjectIntroduction";
import PublicationsTable from "../PublicationsTable";
import { useAtom } from "jotai";
import {
  searchStringAtom,
  registrationAtom,
  publicationsAtom,
  loadingRegAtom,
  errorAtom,
  modelAtom,
  reasoningAtom,
} from "../../state/atoms";
import { useNavigate, useParams } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";

function App() {
  const [searchString, setSearchString] = useAtom(searchStringAtom);
  const [registration, setRegistration] = useAtom(registrationAtom);
  const [publications, setPublications] = useAtom(publicationsAtom);
  const [loading, setLoading] = useAtom(loadingRegAtom);
  const [error, setError] = useAtom(errorAtom);
  const [model, setModel] = useAtom(modelAtom);
  const [reasoning, setReasoning] = useAtom(reasoningAtom);
  const [hasSearched, setHasSearched] = useState(false);

  const navigate = useNavigate();
  const { trialId } = useParams();

  const onSearchChange = (e) => {
    setSearchString(e?.target?.value);
    setHasSearched(false);
  };

  const isValidTrialId = (id) =>
    /^(NCT\d{8}|DRKS\d{8}|\d{4}-\d{6}-\d{2})$/.test(id);

  const buildQueryString = useCallback(() => {
    const params = new URLSearchParams();
    if (model) params.set("model", model);
    if (reasoning) params.set("reasoning", reasoning);
    const qs = params.toString();
    return qs ? `?${qs}` : "";
  }, [model, reasoning]);

  const fetchData = useCallback(async (id) => {
    if (!isValidTrialId(id)) {
      setError(
        "Invalid trial ID format. Please enter an NCT ID (NCT12345678), DRKS ID (DRKS00003623), or EUDRACT ID (2020-123456-12)."
      );
      return;
    }
    setError(null);
    setLoading(true);
    setHasSearched(true);
    setPublications([]);
    try {
      const regResponse = await fetch(`/api/trials/${id}`);
      const regData = await regResponse.json();
      setRegistration(regData);

      if (regData?.trialId) {
        const qs = buildQueryString();
        const pubsResponse = await fetch(`/api/publications/${regData.trialId}${qs}`);
        const { pubs, errors } = await pubsResponse.json();
        setPublications(pubs);
        if (errors?.length > 0) {
          setError(errors);
        }
      }
    } catch (error) {
      console.error("Error:", error);
      setError(error);
    } finally {
      setLoading(false);
    }
  }, [setError, setLoading, setRegistration, setPublications, buildQueryString]);

  const handleManualSearch = async () => {
    if (!isValidTrialId(searchString)) {
      setError(
        "Invalid trial ID format. Please enter an NCT ID (NCT12345678), DRKS ID (DRKS00003623), or EUDRACT ID (2020-123456-12)."
      );
      return;
    }
    await fetchData(searchString);
    navigate(`/trialscout/${searchString}`, { replace: true });
  };

  useEffect(() => {
    if (trialId && isValidTrialId(trialId)) {
      setSearchString(trialId);
      fetchData(trialId);
    }
  }, [trialId, setSearchString, fetchData]);

  console.log({ publications });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const noRegistrationFound =
    !loading && searchString && !registration?.trialId && hasSearched;
  const noPublicationsFound =
    !loading && registration?.trialId && publications?.length === 0;

  return (
    <Container className="App" sx={{ marginBottom: 8 }} maxWidth={false}>
      <ProjectIntroduction />

      <Collapse in={!!error}>
        <Alert
          severity="error"
          sx={{ marginTop: 2, maxWidth: "800px", marginX: "auto" }}
        >
          {Array.isArray(error)
            ? error.map((e) => (typeof e === "string" ? e : e.error || e.message || String(e))).join(", ")
            : typeof error === "string"
              ? error
              : error?.message || String(error)}
        </Alert>
      </Collapse>

      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
          marginTop: 2,
          flexWrap: "wrap",
        }}
      >
        <Search
          value={searchString}
          onChange={onSearchChange}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !loading) {
              handleManualSearch();
            }
          }}
          sx={{ maxWidth: "800px", margin: 1 }}
        />

        <FormControl size="small" sx={{ minWidth: 140, margin: 1 }}>
          <InputLabel>Model</InputLabel>
          <Select
            value={model}
            label="Model"
            onChange={(e) => setModel(e.target.value)}
          >
            <MenuItem value="gpt-5.1">GPT-5.1</MenuItem>
            <MenuItem value="gpt-5-mini-2025-08-07">GPT-5 mini</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 140, margin: 1 }}>
          <InputLabel>Reasoning</InputLabel>
          <Select
            value={reasoning}
            label="Reasoning"
            onChange={(e) => setReasoning(e.target.value)}
          >
            <MenuItem value="minimal">Minimal</MenuItem>
            <MenuItem value="low">Low</MenuItem>
            <MenuItem value="medium">Medium</MenuItem>
            <MenuItem value="high">High</MenuItem>
          </Select>
        </FormControl>

        <Button
          onClick={() => handleManualSearch()}
          sx={{ margin: 1 }}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? "Loading..." : "Search TrialScout"}
        </Button>
      </Box>

      {registration &&
        !noRegistrationFound &&
        (isMobile ? (
          <TrialAccordion data={registration} />
        ) : (
          <TrialDetails data={registration} />
        ))}

      <Collapse in={noRegistrationFound}>
        <Alert
          severity="error"
          sx={{ marginTop: 4, maxWidth: "800px", marginX: "auto" }}
        >
          No registration found for trial ID "{searchString}".
        </Alert>
      </Collapse>

      {publications?.length > 0 && (
        <PublicationsTable
          publications={publications}
          registration={registration}
          buildQueryString={buildQueryString}
        />
      )}
      <Collapse in={noPublicationsFound}>
        <Alert
          severity="warning"
          sx={{ marginTop: 4, maxWidth: "800px", marginX: "auto" }}
        >
          No publications found for trial ID "{searchString}".
        </Alert>
      </Collapse>
    </Container>
  );
}

export default App;
